import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLPerformanceOpt = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Best Practices</div>
                <h1>HTML Performance Optimization</h1>
                <p className="hero-lead">
                    Optimize HTML for faster page loads and better user experience. Learn techniques for
                    reducing page weight, improving render speed, and maximizing web performance.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Performance Matters</h2>
                <div className="stats-grid">
                    <div className="stat-card">
                        <h3>53%</h3>
                        <p>of users abandon sites that take {"&gt;"} 3 seconds to load</p>
                    </div>
                    <div className="stat-card">
                        <h3>1 second</h3>
                        <p>delay = 7% reduction in conversions</p>
                    </div>
                    <div className="stat-card">
                        <h3>100ms</h3>
                        <p>faster = 1% increase in revenue (Amazon)</p>
                    </div>
                    <div className="stat-card">
                        <h3>Top 3</h3>
                        <p>Google ranking factor for search</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Critical Rendering Path</h2>
                <p>Understanding how browsers render HTML is key to optimization:</p>

                <ol className="rendering-steps">
                    <li><strong>Parse HTML</strong>  Build DOM tree</li>
                    <li><strong>Parse CSS</strong>  Build CSSOM tree</li>
                    <li><strong>Combine DOM + CSSOM</strong>  Render tree</li>
                    <li><strong>Layout</strong>  Calculate positions</li>
                    <li><strong>Paint</strong>  Draw pixels</li>
                </ol>

                <p><strong>Goal:</strong> Minimize blocking resources and reduce time to first paint.</p>
            </div>

            <div className="content-section">
                <h2>Optimize HTML Document Structure</h2>

                <h3>Optimized HTML Template</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- Essential meta tags first --&gt;
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    
    <!-- Preconnect to external domains --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://cdn.example.com">
    
    <!-- Preload critical resources --&gt;
    <link rel="preload" href="/fonts/main.woff2" as="font" type="font/woff2" crossorigin>
    <link rel="preload" href="/images/hero.jpg" as="image">
    
    <!-- Critical CSS inline --&gt;
    <style>
        /* Above-the-fold CSS here */
        body { margin: 0; font-family: sans-serif; }
        .hero { height: 100vh; background: #333; }
    </style>
    
    <title>Page Title</title>
    
    <!-- Defer non-critical CSS --&gt;
    <link rel="stylesheet" href="/css/main.css" media="print" onload="this.media='all'">
    <noscript><link rel="stylesheet" href="/css/main.css"></noscript>
</head>
<body>
    <!-- Content here --&gt;
    
    <!-- Scripts at end with defer/async --&gt;
    <script src="/js/main.js" defer></script>
    <script src="/js/analytics.js" async></script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Optimize Images</h2>

                <h3>Lazy Loading</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Native lazy loading (modern browsers) --&gt;
<img src="image.jpg" 
     alt="Description" 
     loading="lazy"
     width="800" 
     height="600">

<!-- Load hero image immediately --&gt;
<img src="hero.jpg" 
     alt="Hero" 
     loading="eager"
     fetchpriority="high">

<!-- Lazy load all below-the-fold images --&gt;
<img src="photo1.jpg" alt="Photo 1" loading="lazy">
<img src="photo2.jpg" alt="Photo 2" loading="lazy">
<img src="photo3.jpg" alt="Photo 3" loading="lazy">`} />
                </div>

                <h3>Responsive Images (srcset)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Serve appropriate image size --&gt;
<img src="image-400w.jpg"
     srcset="image-400w.jpg 400w,
             image-800w.jpg 800w,
             image-1200w.jpg 1200w"
     sizes="(max-width: 600px) 100vw,
            (max-width: 900px) 50vw,
            800px"
     alt="Responsive image"
     loading="lazy">`} />
                </div>

                <h3>Modern Image Formats</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Use WebP with fallback --&gt;
<picture>
    <source type="image/webp" srcset="image.webp">
    <source type="image/jpeg" srcset="image.jpg">
    <img src="image.jpg" alt="Image" loading="lazy">
</picture>

<!-- Or AVIF (even better compression) --&gt;
<picture>
    <source type="image/avif" srcset="image.avif">
    <source type="image/webp" srcset="image.webp">
    <img src="image.jpg" alt="Image" loading="lazy">
</picture>`} />
                </div>

                <h3>Image Dimensions</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Always specify width and height (prevents layout shift) --&gt;
<img src="image.jpg" 
     alt="Description"
     width="800" 
     height="600"
     loading="lazy">

<!-- Use aspect-ratio in CSS --&gt;
<style>
img {
    aspect-ratio: 16 / 9;
    width: 100%;
    height: auto;
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Optimize JavaScript Loading</h2>

                <h3>defer vs async vs blocking</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!--  Blocking (blocks HTML parsing) --&gt;
<script src="script.js"></script>

<!-- async: Download in parallel, execute ASAP --&gt;
<script src="analytics.js" async></script>
<!-- Good for: Independent scripts (analytics, ads) --&gt;

<!-- defer: Download in parallel, execute after HTML parsed --&gt;
<script src="app.js" defer></script>
<!-- Good for: Scripts that need DOM, maintain order --&gt;

<!-- Multiple deferred scripts execute in order --&gt;
<script src="library.js" defer></script>
<script src="app.js" defer></script>  <!-- Runs after library.js --&gt;

<!-- Put scripts at end of body --&gt;
<body>
    <!-- Content --&gt;
    <script src="script.js"></script>
</body>`} />
                </div>

                <h3>Inline Critical JavaScript</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Inline small, critical scripts --&gt;
<script>
    // Feature detection
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('/sw.js');
    }
    
    // Critical functionality
    const isDarkMode = localStorage.getItem('theme') === 'dark';
    if (isDarkMode) {
        document.documentElement.classList.add('dark');
    }
</script>

<!-- Load non-critical scripts later --&gt;
<script src="non-critical.js" defer></script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Optimize CSS Loading</h2>

                <h3>Critical CSS Inline</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Inline critical above-the-fold CSS --&gt;
    <style>
        /* Only styles needed for initial render */
        body {
            margin: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .hero {
            min-height: 100vh;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
    </style>
    
    <!-- Defer non-critical CSS --&gt;
    <link rel="stylesheet" href="main.css" media="print" onload="this.media='all'">
    <noscript><link rel="stylesheet" href="main.css"></noscript>
</head>`} />
                </div>

                <h3>Avoid CSS @import</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<!--  CSS @import blocks rendering --&gt;
<style>
    @import url('other.css');
</style>`} />
                </div>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Use link tags instead --&gt;
<link rel="stylesheet" href="main.css">
<link rel="stylesheet" href="other.css">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Resource Hints</h2>

                <h3>Preconnect</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Establish early connections to important origins --&gt;
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://cdn.example.com">
<link rel="preconnect" href="https://api.example.com">`} />
                </div>

                <h3>DNS Prefetch</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Resolve DNS ahead of time --&gt;
<link rel="dns-prefetch" href="https://analytics.google.com">
<link rel="dns-prefetch" href="https://www.googletagmanager.com">`} />
                </div>

                <h3>Preload</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Preload critical resources --&gt;
<link rel="preload" href="/fonts/main.woff2" as="font" type="font/woff2" crossorigin>
<link rel="preload" href="/hero-image.jpg" as="image">
<link rel="preload" href="/critical.css" as="style">
<link rel="preload" href="/app.js" as="script">`} />
                </div>

                <h3>Prefetch</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Prefetch resources for next navigation --&gt;
<link rel="prefetch" href="/next-page.html">
<link rel="prefetch" href="/images/next-page-hero.jpg">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Minimize HTML Size</h2>

                <h3>Remove Unnecessary Code</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<!--  Bloated HTML --&gt;
<div class="container">
    <div class="wrapper">
        <div class="inner">
            <div class="content">
                <p>Text</p>
            </div>
        </div>
    </div>
</div>

<!-- Excessive comments --&gt;
<!-- This is a div --&gt;
<!-- This contains content --&gt;
<div>Content</div>`} />
                </div>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Minimal HTML --&gt;
<div class="container">
    <p>Text</p>
</div>

<!-- Useful comments only --&gt;
<!-- Product Grid Section --&gt;
<div class="product-grid">...</div>`} />
                </div>

                <h3>Minify HTML</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Before minification (2.5KB) --&gt;
<!DOCTYPE html>
<html>
    <head>
        <title>Page</title>
    </head>
    <body>
        <div class="container">
            <p>Content</p>
        </div>
    </body>
</html>

<!-- After minification (1.8KB) --&gt;
<!DOCTYPE html><html><head><title>Page</title></head><body><div class="container"><p>Content</p></div></body></html>

<!-- Use build tools:
   - html-minifier
   - terser
   - Webpack/Vite plugins --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Reduce Render-Blocking Resources</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Critical resources only --&gt;
    <style>
        /* Critical CSS inline */
    </style>
    
    <!--  Don't block rendering with non-critical CSS --&gt;
    <!-- <link rel="stylesheet" href="large.css"> --&gt;
    
    <!-- Defer non-critical CSS --&gt;
    <link rel="stylesheet" href="large.css" media="print" onload="this.media='all'">
    
    <!--  Don't load sync scripts in head --&gt;
    <!-- <script src="app.js"></script> --&gt;
    
    <!-- Defer or async scripts --&gt;
    <script src="app.js" defer></script>
</head>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Optimize Web Fonts</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Preconnect to font provider --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    
    <!-- Load fonts with font-display --&gt;
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
    
    <!-- Or self-host fonts --&gt;
    <style>
        @font-face {
            font-family: 'Roboto';
            src: url('/fonts/roboto.woff2') format('woff2');
            font-display: swap;  /* Show fallback font first */
            font-weight: 400;
        }
    </style>
</head>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Reduce Layout Shift (CLS)</h2>

                <h3>Reserve Space for Images</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Always specify dimensions --&gt;
<img src="image.jpg" 
     alt="Description"
     width="800" 
     height="600">

<!-- Or use CSS aspect-ratio --&gt;
<style>
.image-container {
    aspect-ratio: 16 / 9;
}
</style>`} />
                </div>

                <h3>Reserve Space for Ads/Embeds</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Reserve space for ad --&gt;
<div class="ad-container" style="min-height: 250px;">
    <!-- Ad loads here --&gt;
</div>

<!-- Reserve space for iframe --&gt;
<div style="aspect-ratio: 16 / 9;">
    <iframe src="video.html" style="width: 100%; height: 100%;"></iframe>
</div>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance Measurement Tools</h2>

                <h3>Google Lighthouse</h3>
                <ul>
                    <li>Open Chrome DevTools (<kbd>F12</kbd>)</li>
                    <li>Go to Lighthouse tab</li>
                    <li>Run audit</li>
                    <li>Get performance score (0-100)</li>
                </ul>

                <h3>Core Web Vitals</h3>
                <ul>
                    <li><strong>LCP (Largest Contentful Paint):</strong> &lt; 2.5s</li>
                    <li><strong>FID (First Input Delay):</strong> &lt; 100ms</li>
                    <li><strong>CLS (Cumulative Layout Shift):</strong> &lt; 0.1</li>
                </ul>

                <h3>Other Tools</h3>
                <ul>
                    <li><strong>WebPageTest:</strong> Detailed performance analysis</li>
                    <li><strong>PageSpeed Insights:</strong> Google's performance tool</li>
                    <li><strong>Chrome DevTools Performance:</strong> Record and analyze</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Performance Checklist</h2>

                <div className="checklist">
                    <h3>HTML</h3>
                    <ul>
                        <li> Minify HTML in production</li>
                        <li> Remove unnecessary divs/wrappers</li>
                        <li> Remove unused code</li>
                        <li> Use semantic HTML</li>
                    </ul>

                    <h3>CSS</h3>
                    <ul>
                        <li> Inline critical CSS</li>
                        <li> Defer non-critical CSS</li>
                        <li> Minify CSS</li>
                        <li> Remove unused CSS</li>
                        <li> Avoid @import</li>
                    </ul>

                    <h3>JavaScript</h3>
                    <ul>
                        <li> Use defer/async</li>
                        <li> Put scripts at end of body</li>
                        <li> Minify JavaScript</li>
                        <li> Code splitting</li>
                        <li> Remove unused JavaScript</li>
                    </ul>

                    <h3>Images</h3>
                    <ul>
                        <li> Lazy load below-the-fold images</li>
                        <li> Use srcset for responsive images</li>
                        <li> Use WebP/AVIF formats</li>
                        <li> Compress images</li>
                        <li> Specify width/height</li>
                        <li> Use appropriate image sizes</li>
                    </ul>

                    <h3>Fonts</h3>
                    <ul>
                        <li> Use font-display: swap</li>
                        <li> Preload critical fonts</li>
                        <li> Use WOFF2 format</li>
                        <li> Limit font variations</li>
                    </ul>

                    <h3>Resources</h3>
                    <ul>
                        <li> Preconnect to external domains</li>
                        <li> Preload critical resources</li>
                        <li> Use CDN for static assets</li>
                        <li> Enable compression (gzip/brotli)</li>
                        <li> Use caching headers</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLPerformanceOpt;




